'use client'

import { useEffect, useState } from 'react'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [serverErrorDetails, setServerErrorDetails] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Log the error to console with full details
    console.error('🚨 Page Error Caught:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      fullError: JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
    })

    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    console.error('🔍 NEXT.JS ERROR DIGEST:', error.digest)
    console.error('📋 TO FIND FULL ERROR IN SERVER LOGS, SEARCH FOR:')
    console.error(`   grep "${error.digest}" <your-log-file>`)
    console.error('   OR search CloudWatch for:', error.digest)
    console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')

    // Try to fetch error details (works if instrumentation captured it)
    if (error.digest) {
      console.log(`🔍 Attempting to fetch server error details for digest: ${error.digest}`)
      fetch(`/api/error-details/${error.digest}`)
        .then(res => res.json())
        .then(data => {
          console.log('📥 Server error lookup result:', data)
          setServerErrorDetails(data)
          setLoading(false)
        })
        .catch(err => {
          console.error('⚠️ Error details not available via API (expected in Next.js 14):', err)
          console.log('💡 Check server logs for digest:', error.digest)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }

    // Send to error logging endpoint
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        errorId: `page_error_${Date.now()}`,
        name: error.name,
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
        environment: process.env.NODE_ENV,
        fullError: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),
      })
    }).catch(err => console.error('Failed to log error:', err))
  }, [error])

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="text-center max-w-4xl w-full">
        <div className="text-6xl mb-4" role="img" aria-label="Error">
          😞
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Something went wrong
        </h2>
        <p className="text-gray-600 mb-4">
          We&apos;re sorry for the inconvenience. The application encountered an unexpected error.
        </p>

        {/* Error Digest ID */}
        {error.digest && (
          <div className="mb-6 p-3 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Error Digest:</strong> <code className="bg-gray-200 px-2 py-1 rounded">{error.digest}</code>
            </p>
          </div>
        )}

        {/* Full Error Details - Always shown */}
        <div className="mb-6 text-left">
          {loading && error.digest && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-700">🔍 Loading full error details from server...</p>
            </div>
          )}
          
          <details open className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <summary className="cursor-pointer font-semibold text-red-900 mb-2 flex items-center gap-2">
              <span>
                {serverErrorDetails?.found 
                  ? 'Full Error Details (Retrieved from Server)' 
                  : 'Error Details (Client-Side)'
                }
              </span>
              <button
                onClick={() => {
                  const errorDetails = serverErrorDetails?.found ? serverErrorDetails : {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                    digest: error.digest,
                    fullError: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),
                  }
                  navigator.clipboard?.writeText(JSON.stringify(errorDetails, null, 2))
                    .then(() => alert('Error details copied to clipboard'))
                    .catch(err => console.error('Failed to copy:', err))
                }}
                className="text-xs bg-red-100 hover:bg-red-200 px-2 py-1 rounded"
                title="Copy error details to clipboard"
              >
                📋 Copy
              </button>
            </summary>
            <div className="space-y-4">
              {/* Server Error Details (if available) */}
              {serverErrorDetails?.found && (
                <>
                  <div className="p-3 bg-green-100 border border-green-300 rounded">
                    <p className="text-green-800 font-semibold">✅ Full server-side error details retrieved!</p>
                    <p className="text-green-700 text-sm">The actual error message from the server is shown below.</p>
                  </div>
                  
                  <div>
                    <strong className="text-red-800">Server Error Name:</strong>
                    <pre className="text-sm text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto">
                      {serverErrorDetails.name || 'Unknown'}
                    </pre>
                  </div>
                  
                  <div>
                    <strong className="text-red-800">Server Error Message (Original):</strong>
                    <pre className="text-sm text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto whitespace-pre-wrap">
                      {serverErrorDetails.message || 'No error message available'}
                    </pre>
                  </div>
                  
                  <div>
                    <strong className="text-red-800">Server Stack Trace:</strong>
                    <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-60">
                      {serverErrorDetails.stack || 'No stack trace available'}
                    </pre>
                  </div>
                  
                  <div>
                    <strong className="text-red-800">Full Server Error Object:</strong>
                    <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-60">
                      {JSON.stringify(serverErrorDetails.fullError, null, 2)}
                    </pre>
                  </div>
                </>
              )}
              
              {/* Client-side error (always show) */}
              <div className="pt-4 border-t border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">Client-Side Error Info:</h4>
              </div>
              
              <div>
                <strong className="text-red-800">Error Name:</strong>
                <pre className="text-sm text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto">
                  {error?.name || 'Unknown'}
                </pre>
              </div>
              
              <div>
                <strong className="text-red-800">Error Message (as received by client):</strong>
                <pre className="text-sm text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto whitespace-pre-wrap">
                  {error?.message || 'No error message available'}
                </pre>
                {error?.message?.includes('omitted in production') && !serverErrorDetails?.found && (
                  <p className="text-xs text-orange-600 mt-2">
                    ⚠️ This is a redacted message. Check server logs for digest: <code className="bg-orange-100 px-1 rounded">{error.digest}</code>
                  </p>
                )}
              </div>
              
              {error.digest && (
                <div>
                  <strong className="text-red-800">Error Digest:</strong>
                  <pre className="text-sm text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto">
                    {error.digest}
                  </pre>
                  <p className="text-xs text-gray-600 mt-1">
                    Search server logs for this digest to find the original error.
                  </p>
                </div>
              )}
              
              <div>
                <strong className="text-red-800">Client Error Stack:</strong>
                <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-60">
                  {error?.stack || 'No stack trace available'}
                </pre>
              </div>
              
              <div>
                <strong className="text-red-800">Full Client Error Object:</strong>
                <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-60">
                  {JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}
                </pre>
              </div>
            </div>
          </details>
          
          {/* Help text if server details not found */}
          {!loading && error.digest && !serverErrorDetails?.found && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 font-semibold mb-3">
                📋 Next.js Redacted This Error (Security Feature)
              </p>
              <p className="text-yellow-700 text-sm mb-3">
                The full error message is in your server logs. Here&apos;s how to find it:
              </p>
              
              <div className="space-y-3">
                <div className="bg-yellow-100 p-3 rounded">
                  <p className="font-semibold text-yellow-900 text-sm mb-1">🔍 Search Term:</p>
                  <code className="text-xs bg-yellow-200 px-2 py-1 rounded block">
                    {error.digest}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(error.digest || '')
                      alert('Digest copied! Paste into log search.')
                    }}
                    className="mt-2 text-xs bg-yellow-200 hover:bg-yellow-300 px-2 py-1 rounded"
                  >
                    📋 Copy Digest
                  </button>
                </div>

                <details className="bg-yellow-100 p-3 rounded">
                  <summary className="font-semibold text-yellow-900 text-sm cursor-pointer">
                    📖 Where to Look (Click to Expand)
                  </summary>
                  <div className="mt-2 space-y-2 text-xs">
                    <div>
                      <p className="font-semibold text-yellow-900">Docker Logs:</p>
                      <code className="bg-yellow-200 px-2 py-1 rounded block mt-1">
                        docker logs $(docker ps -q) 2&gt;&1 | grep &quot;{error.digest}&quot;
                      </code>
                    </div>
                    <div>
                      <p className="font-semibold text-yellow-900">CloudWatch (ECS):</p>
                      <code className="bg-yellow-200 px-2 py-1 rounded block mt-1">
                        aws logs filter-log-events --log-group-name /aws/ecs/fishing-club --filter-pattern &quot;{error.digest}&quot;
                      </code>
                    </div>
                    <div>
                      <p className="font-semibold text-yellow-900">AWS Console:</p>
                      <a 
                        href={`https://ap-southeast-2.console.aws.amazon.com/cloudwatch/home?region=ap-southeast-2#logsV2:log-groups`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline block mt-1"
                      >
                        Open CloudWatch Logs →
                      </a>
                    </div>
                  </div>
                </details>

                <div className="bg-orange-100 p-3 rounded border border-orange-300">
                  <p className="font-semibold text-orange-900 text-sm mb-1">⚡ Common Issues:</p>
                  <ul className="text-xs text-orange-800 space-y-1 list-disc ml-4">
                    <li><strong>NEXTAUTH_SECRET not set</strong> → Check environment variables</li>
                    <li><strong>DATABASE_URL not set</strong> → Check AWS Secrets Manager</li>
                    <li><strong>Database connection failed</strong> → Check RDS security groups</li>
                    <li><strong>Prisma errors</strong> → Run migrations</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <details className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <summary className="cursor-pointer font-semibold text-blue-900 mb-2">
              System Information
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong className="text-blue-800">Timestamp:</strong>
                <p className="text-blue-700">{new Date().toISOString()}</p>
              </div>
              <div>
                <strong className="text-blue-800">URL:</strong>
                <p className="text-blue-700 break-all">
                  {typeof window !== 'undefined' ? window.location.href : 'Unknown'}
                </p>
              </div>
              <div className="md:col-span-2">
                <strong className="text-blue-800">User Agent:</strong>
                <p className="text-blue-700 text-xs break-all">
                  {typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'}
                </p>
              </div>
              <div>
                <strong className="text-blue-800">Environment:</strong>
                <p className="text-blue-700">{process.env.NODE_ENV}</p>
              </div>
            </div>
          </details>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={reset} variant="secondary">
            Try Again
          </Button>
          <Button onClick={() => window.location.reload()}>
            Reload Page
          </Button>
          <Button onClick={() => window.location.href = '/'}>
            Go Home
          </Button>
        </div>

        <p className="mt-6 text-sm text-gray-500">
          If this problem persists, please contact support with the error details above.
        </p>
      </Card>
    </div>
  )
}

